/**
 * RefineChatPanel — a slide-out panel that lets the user follow up on an
 * Intelligence analysis with chat, or generate a refined PowerPoint / Excel.
 *
 * Mirrored from AI Council's Refine & Chat behaviour (frontend/src/pages/
 * AICouncil.tsx) but built as a standalone component so Data Reconciliation
 * and Smart Data Analyst can both reuse the exact same UX without touching
 * AI Council's code path. The AI Council panel stays untouched.
 *
 * What the panel does:
 *   - Chat mode: streams a follow-up answer grounded in the existing analysis.
 *   - Presentation mode: regenerates a .pptx from the analysis + user request
 *     ("page 1 should show X, page 2 should show Y"). Iterative — every send
 *     calls the backend fresh with the latest user instructions.
 *   - Excel mode: regenerates a styled .xlsx from the analysis + user request.
 *
 * All three modes share one stateless backend so the user can iterate freely;
 * each call posts the full analysis context + their refinement request.
 */

import React, { useEffect, useRef, useState } from 'react';
import {
  MessageSquare, X, Send, Loader2, Download, Presentation, FileText,
  BarChart2, PlusCircle,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import html2canvas from 'html2canvas';
import { MD_TABLE_COMPONENTS } from '../Common/MarkdownContent';
import DashboardRenderer, { ChartSpec } from '../DashboardRenderer';
import toast from 'react-hot-toast';
import api from '../../api';

// ─── Public types ──────────────────────────────────────────────────────────

export type RefineMode = 'data-cleaning' | 'smart-analyst';

export interface RefineChatContext {
  /** The user's original prompt / goal for this analysis. */
  sessionPrompt: string;
  /** The main analysis text we feed back as grounding context. */
  analysisText: string;
  sessionId?: number | string | null;
}

export interface RefineChatPanelProps {
  open: boolean;
  onClose: () => void;
  /** Identifies the source module so the backend can tailor prompts/exports. */
  mode: RefineMode;
  /**
   * Called every time the panel needs fresh context. We re-invoke this on
   * every send so updates to the parent's state (e.g. the user re-running
   * the analysis) are picked up without remounting the panel.
   */
  contextProvider: () => RefineChatContext;
  /** Brand colour for the chat mode + send button (PPT/Excel keep their own). */
  accentColor?: string;
  /** Empty-state suggestion chips. Defaults provided per mode. */
  suggestionChips?: { label: string; prompt: string }[];
  /**
   * Opt-in "Chart" mode. When enabled, a Chart tab appears that builds a
   * visualization from this session's data (POST /generate-chart) and renders
   * it inline. Only meaningful for Smart Data Analyst, which has a saved
   * session + an on-screen dashboard to push charts into. Leave undefined for
   * Data Cleaning so its panel is unchanged.
   */
  enableChart?: boolean;
  /** Raw rows backing the session — passed to DashboardRenderer for charting. */
  chartRawData?: any[];
  /**
   * Adds a generated chart to the parent's live dashboard. When provided, an
   * "Add to dashboard" button shows under each chart. Omit to hide it.
   */
  onAddChartToDashboard?: (chart: ChartSpec) => void;
}

// ─── Internal types ────────────────────────────────────────────────────────

type ChatMode = 'chat' | 'chart' | 'pptx' | 'excel' | 'word';
type ChatRole = 'user' | 'assistant';
interface ChatMessage { role: ChatRole; content: string; chart?: ChartSpec }

// Wire format for assistant messages that carry a downloadable file:
//   __PPTX__|||<blobUrl>|||<filename>
//   __EXCEL__|||<blobUrl>|||<filename>
//   __WORD__|||<blobUrl>|||<filename>
const PPTX_MARKER  = '__PPTX__|||';
const EXCEL_MARKER = '__EXCEL__|||';
const WORD_MARKER  = '__WORD__|||';

// ─── Defaults ──────────────────────────────────────────────────────────────

const DEFAULT_SUGGESTIONS: Record<RefineMode, { label: string; prompt: string }[]> = {
  'data-cleaning': [
    { label: '📊 Summarize cleaning issues', prompt: 'Summarize the top 5 data quality issues found and the recommended action for each.' },
    { label: '✅ Top 3 next steps',          prompt: 'Based on the cleaning results, what are the top 3 concrete next steps the team should take?' },
    { label: '🔍 Stakeholder questions',     prompt: 'List the most important questions to ask stakeholders to resolve the remaining ambiguities in this data.' },
  ],
  'smart-analyst': [
    { label: '📋 Top 5 takeaways', prompt: 'List the top 5 takeaways from this analysis in concise bullets.' },
    { label: '⚠️ Key risks',        prompt: 'What are the biggest risks or red flags surfaced in this analysis?' },
    { label: '🎯 Recommend next steps', prompt: 'Recommend the next 3 concrete actions the team should take, with owners.' },
  ],
};

// ─── Component ─────────────────────────────────────────────────────────────

const RefineChatPanel: React.FC<RefineChatPanelProps> = ({
  open, onClose, mode, contextProvider,
  accentColor = '#4F46E5',
  suggestionChips,
  enableChart = false,
  chartRawData,
  onAddChartToDashboard,
}) => {
  const [messages, setMessages]             = useState<ChatMessage[]>([]);
  const [input, setInput]                   = useState('');
  const [chatMode, setChatMode]             = useState<ChatMode>('chat');
  const [chatLoading, setChatLoading]       = useState(false);
  const [pptxGenerating, setPptxGenerating] = useState(false);
  const [excelGenerating, setExcelGenerating] = useState(false);
  const [wordGenerating, setWordGenerating]   = useState(false);
  const [chartGenerating, setChartGenerating] = useState(false);
  const [downloadingUrl, setDownloadingUrl] = useState<string | null>(null);
  const chatEndRef                          = useRef<HTMLDivElement>(null);

  // Auto-scroll to the bottom on every new message or generation tick.
  useEffect(() => {
    if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages, chatLoading, pptxGenerating, excelGenerating, wordGenerating, chartGenerating]);

  // ── Helpers ──────────────────────────────────────────────────────────────

  const handleFileDownload = (url: string, filename: string) => {
    setDownloadingUrl(url);
    try {
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } finally {
      setTimeout(() => setDownloadingUrl(null), 600);
    }
  };

  const assertAnalysis = (): RefineChatContext | null => {
    const ctx = contextProvider();
    if (!ctx.analysisText || !ctx.analysisText.trim()) {
      toast.error('No analysis available yet to refine');
      return null;
    }
    return ctx;
  };

  // ── Backend calls ────────────────────────────────────────────────────────

  const handleGeneratePptx = async (userRequest: string) => {
    const ctx = assertAnalysis();
    if (!ctx) return;
    setPptxGenerating(true);
    // Capture the full conversation INCLUDING this request so the backend can
    // accumulate every prior refinement (page structure, slide count changes,
    // content edits) rather than only seeing the latest message.
    const convo = [...messages, { role: 'user' as const, content: userRequest }];
    setMessages(convo);
    try {
      const res = await api.post(
        '/api/intelligence/refine-chat/generate-pptx',
        { mode, sessionPrompt: ctx.sessionPrompt, analysisText: ctx.analysisText, userRequest, messages: convo, sessionId: ctx.sessionId ?? undefined },
        { responseType: 'blob', timeout: 720000 },
      );
      const blobUrl  = window.URL.createObjectURL(new Blob([res.data]));
      const filename = (res.headers['x-file-name'] as string) || `refined-${Date.now()}.pptx`;
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `${PPTX_MARKER}${blobUrl}|||${filename}`,
      }]);
      toast.success('Presentation ready!');
    } catch (err: any) {
      let message = 'Failed to generate presentation';
      if (err?.response?.data instanceof Blob) {
        try { message = JSON.parse(await err.response.data.text()).error || message; } catch { /* ignore */ }
      } else if (err?.response?.data?.error) {
        message = err.response.data.error;
      }
      setMessages(prev => [...prev, { role: 'assistant', content: `❌ ${message}` }]);
      toast.error(message);
    } finally {
      setPptxGenerating(false);
    }
  };

  const handleGenerateExcel = async (userRequest: string) => {
    const ctx = assertAnalysis();
    if (!ctx) return;
    setExcelGenerating(true);
    setMessages(prev => [...prev, { role: 'user', content: userRequest }]);
    try {
      const res = await api.post(
        '/api/intelligence/refine-chat/generate-excel',
        { mode, sessionPrompt: ctx.sessionPrompt, analysisText: ctx.analysisText, userRequest },
        { responseType: 'blob', timeout: 180000 },
      );
      const blobUrl  = window.URL.createObjectURL(new Blob([res.data]));
      const filename = (res.headers['x-file-name'] as string) || `refined-${Date.now()}.xlsx`;
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `${EXCEL_MARKER}${blobUrl}|||${filename}`,
      }]);
      toast.success('Excel file ready!');
    } catch (err: any) {
      let message = 'Failed to generate Excel file';
      if (err?.response?.data instanceof Blob) {
        try { message = JSON.parse(await err.response.data.text()).error || message; } catch { /* ignore */ }
      } else if (err?.response?.data?.error) {
        message = err.response.data.error;
      }
      setMessages(prev => [...prev, { role: 'assistant', content: `❌ ${message}` }]);
      toast.error(message);
    } finally {
      setExcelGenerating(false);
    }
  };

  const handleGenerateWord = async (userRequest: string) => {
    const ctx = assertAnalysis();
    if (!ctx) return;
    setWordGenerating(true);
    setMessages(prev => [...prev, { role: 'user', content: userRequest || 'Download as Word document' }]);
    try {
      const res = await api.post(
        '/api/intelligence/refine-chat/generate-word',
        { mode, sessionPrompt: ctx.sessionPrompt, analysisText: ctx.analysisText, userRequest },
        { responseType: 'blob', timeout: 180000 },
      );
      const blobUrl  = window.URL.createObjectURL(new Blob([res.data]));
      const filename = (res.headers['x-file-name'] as string) || `refined-${Date.now()}.docx`;
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `${WORD_MARKER}${blobUrl}|||${filename}`,
      }]);
      toast.success('Word document ready!');
    } catch (err: any) {
      let message = 'Failed to generate Word document';
      if (err?.response?.data instanceof Blob) {
        try { message = JSON.parse(await err.response.data.text()).error || message; } catch { /* ignore */ }
      } else if (err?.response?.data?.error) {
        message = err.response.data.error;
      }
      setMessages(prev => [...prev, { role: 'assistant', content: `❌ ${message}` }]);
      toast.error(message);
    } finally {
      setWordGenerating(false);
    }
  };

  // Chart mode — build a visualization from the saved session's data and render
  // it inline. Requires a sessionId (the backend reads the session's rows).
  const handleGenerateChart = async (userRequest: string) => {
    const ctx = contextProvider();
    if (!ctx.sessionId) {
      toast.error('Chart generation needs a saved Smart Data Analyst session.');
      return;
    }
    setChartGenerating(true);
    setMessages(prev => [...prev, { role: 'user', content: userRequest }]);
    try {
      const res = await api.post('/api/intelligence/generate-chart', {
        sessionId: ctx.sessionId,
        request: userRequest,
      });
      if (res.data?.success && res.data.data?.chart) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: 'Here is the chart built from this session’s data:',
          chart: res.data.data.chart as ChartSpec,
        }]);
      } else {
        const message = res.data?.error || 'Could not build that chart.';
        setMessages(prev => [...prev, { role: 'assistant', content: `❌ ${message}` }]);
        toast.error(message);
      }
    } catch (err: any) {
      const message = err?.response?.data?.error || 'Could not build that chart.';
      setMessages(prev => [...prev, { role: 'assistant', content: `❌ ${message}` }]);
      toast.error(message);
    } finally {
      setChartGenerating(false);
    }
  };

  const handleDownloadChart = async (chartElId: string, title: string) => {
    const el = document.getElementById(chartElId);
    if (!el) return;
    try {
      const canvas = await html2canvas(el, { scale: 2, backgroundColor: '#ffffff' });
      const link = document.createElement('a');
      link.download = `${(title || 'chart').replace(/[^a-z0-9]+/gi, '_').slice(0, 60)}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch {
      toast.error('Could not download the chart image.');
    }
  };

  const handleSend = async () => {
    if (!input.trim() || chatLoading || pptxGenerating || excelGenerating || wordGenerating || chartGenerating) return;
    const userRequest = input.trim();
    setInput('');
    if (chatMode === 'chart') { handleGenerateChart(userRequest); return; }
    if (chatMode === 'pptx')  { handleGeneratePptx(userRequest);  return; }
    if (chatMode === 'excel') { handleGenerateExcel(userRequest); return; }
    if (chatMode === 'word')  { handleGenerateWord(userRequest);  return; }

    // Chat mode.
    const ctx = assertAnalysis();
    if (!ctx) return;
    const nextMessages = [...messages, { role: 'user' as ChatRole, content: userRequest }];
    setMessages(nextMessages);
    setChatLoading(true);
    try {
      const res = await api.post('/api/intelligence/refine-chat/chat', {
        mode,
        sessionPrompt: ctx.sessionPrompt,
        analysisText:  ctx.analysisText,
        messages:      nextMessages,
      });
      if (res.data?.success) {
        const reply = String(res.data.data?.reply || '').trim();
        setMessages(prev => [...prev, { role: 'assistant', content: reply || '(no reply)' }]);
      } else {
        const err = res.data?.error || 'Chat failed';
        setMessages(prev => [...prev, { role: 'assistant', content: `❌ ${err}` }]);
        toast.error(err);
      }
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.message || 'Chat failed';
      setMessages(prev => [...prev, { role: 'assistant', content: `❌ ${message}` }]);
      toast.error(message);
    } finally {
      setChatLoading(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────

  if (!open) return null;

  const chips = suggestionChips || DEFAULT_SUGGESTIONS[mode];
  const PPTX_COLOR  = '#F97316';
  const EXCEL_COLOR = '#16A34A';
  const WORD_COLOR  = '#2563EB';
  const CHART_COLOR = '#4F46E5';
  const sendColor = chatMode === 'pptx'  ? PPTX_COLOR
    : chatMode === 'excel' ? EXCEL_COLOR
    : chatMode === 'word'  ? WORD_COLOR
    : chatMode === 'chart' ? CHART_COLOR
    : accentColor;
  const placeholderByMode: Record<ChatMode, string> = {
    chat:  'Ask a follow-up question…',
    chart: 'Describe the chart you want from this data… (e.g. "revenue by region as a donut")',
    pptx:  'Describe what you want in the presentation… (e.g. "page 1 has X, page 2 has Y")',
    excel: 'Describe what data you want in Excel…',
    word:  'Press send to download the full analysis as a Word document, or describe a refinement…',
  };
  // Chart mode is only offered when the parent opts in (Smart Data Analyst).
  const availableModes: ChatMode[] = enableChart
    ? ['chat', 'chart', 'pptx', 'excel', 'word']
    : ['chat', 'pptx', 'excel', 'word'];

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/20 z-[55]" onClick={onClose} />

      {/* Slide-out panel */}
      <div className="fixed top-0 right-0 bottom-0 w-[640px] max-w-[95vw] bg-white shadow-2xl z-[60] flex flex-col border-l border-gray-200">
        {/* Header */}
        <div className="border-b border-gray-100 bg-gradient-to-r from-gray-50 to-slate-50 flex items-center justify-between px-5 py-3 flex-shrink-0">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-4 h-4" style={{ color: accentColor }} />
            <span className="text-sm font-semibold text-gray-800">Refine for Export</span>
          </div>
          <div className="flex items-center gap-1">
            {messages.length > 0 && (
              <button
                onClick={() => setMessages([])}
                className="px-2 py-1 hover:bg-gray-100 rounded-lg text-xs font-medium text-gray-400 hover:text-red-500 transition-colors"
                title="Clear chat"
              >
                Clear Chat
              </button>
            )}
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {messages.length === 0 && (
            <div className="py-8 px-2 space-y-4">
              <div className="text-center">
                <MessageSquare className="w-7 h-7 text-gray-300 mx-auto mb-2" />
                <p className="text-sm font-semibold text-gray-600">Refine the analysis</p>
                <p className="text-xs text-gray-400 mt-1">
                  {enableChart
                    ? 'Ask follow-up questions, build a chart from this data, or generate a presentation / Excel / Word grounded in this analysis.'
                    : 'Ask follow-up questions or generate a presentation / Excel grounded in this analysis.'}
                </p>
              </div>
              <div className="flex flex-col gap-2">
                {chips.map((chip) => (
                  <button
                    key={chip.label}
                    onClick={() => setInput(chip.prompt)}
                    className="text-left px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors"
                  >
                    {chip.label}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((msg, i) => (
            <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              {msg.role === 'user' ? (
                <div
                  className="max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed text-white"
                  style={{ backgroundColor: accentColor }}
                >
                  {msg.content}
                </div>
              ) : msg.content.startsWith(PPTX_MARKER) ? (() => {
                const [blobUrl, filename] = msg.content.replace(PPTX_MARKER, '').split('|||');
                return (
                  <div className="w-full rounded-xl px-4 py-3 bg-indigo-50 border border-indigo-200 text-gray-700 text-sm flex items-center gap-3">
                    <Presentation className="w-5 h-5 text-indigo-600 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-indigo-800 text-sm">Presentation ready</p>
                      <p className="text-xs text-indigo-600 truncate">{filename}</p>
                    </div>
                    <button
                      onClick={() => handleFileDownload(blobUrl, filename)}
                      disabled={downloadingUrl === blobUrl}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white text-xs font-semibold rounded-lg hover:bg-indigo-700 flex-shrink-0 disabled:opacity-60 transition-colors"
                    >
                      {downloadingUrl === blobUrl
                        ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        : <Download className="w-3.5 h-3.5" />}
                      {downloadingUrl === blobUrl ? 'Downloading…' : 'Download'}
                    </button>
                  </div>
                );
              })() : msg.content.startsWith(EXCEL_MARKER) ? (() => {
                const [blobUrl, filename] = msg.content.replace(EXCEL_MARKER, '').split('|||');
                return (
                  <div className="w-full rounded-xl px-4 py-3 bg-green-50 border border-green-200 text-gray-700 text-sm flex items-center gap-3">
                    <Download className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-green-800 text-sm">Excel file ready</p>
                      <p className="text-xs text-green-600 truncate">{filename}</p>
                    </div>
                    <button
                      onClick={() => handleFileDownload(blobUrl, filename)}
                      disabled={downloadingUrl === blobUrl}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white text-xs font-semibold rounded-lg hover:bg-green-700 flex-shrink-0 disabled:opacity-60 transition-colors"
                    >
                      {downloadingUrl === blobUrl
                        ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        : <Download className="w-3.5 h-3.5" />}
                      {downloadingUrl === blobUrl ? 'Downloading…' : 'Download'}
                    </button>
                  </div>
                );
              })() : msg.content.startsWith(WORD_MARKER) ? (() => {
                const [blobUrl, filename] = msg.content.replace(WORD_MARKER, '').split('|||');
                return (
                  <div className="w-full rounded-xl px-4 py-3 bg-blue-50 border border-blue-200 text-gray-700 text-sm flex items-center gap-3">
                    <FileText className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-blue-800 text-sm">Word document ready</p>
                      <p className="text-xs text-blue-600 truncate">{filename}</p>
                    </div>
                    <button
                      onClick={() => handleFileDownload(blobUrl, filename)}
                      disabled={downloadingUrl === blobUrl}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700 flex-shrink-0 disabled:opacity-60 transition-colors"
                    >
                      {downloadingUrl === blobUrl
                        ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        : <Download className="w-3.5 h-3.5" />}
                      {downloadingUrl === blobUrl ? 'Downloading…' : 'Download'}
                    </button>
                  </div>
                );
              })() : msg.chart ? (
                <div className="w-full rounded-xl px-4 py-3 bg-gray-50 border border-gray-200 text-gray-700 text-sm">
                  <p className="mb-2">{msg.content}</p>
                  <div id={`refine-chat-chart-${i}`} className="bg-white rounded-lg border border-gray-200 p-2">
                    <DashboardRenderer
                      spec={{ title: '', charts: [msg.chart] }}
                      forceTwoColumn
                      rawData={chartRawData}
                    />
                  </div>
                  <div className="flex gap-2 mt-2">
                    {onAddChartToDashboard && (
                      <button
                        onClick={() => onAddChartToDashboard(msg.chart as ChartSpec)}
                        className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg text-white transition-colors"
                        style={{ backgroundColor: CHART_COLOR }}
                      >
                        <PlusCircle className="w-3.5 h-3.5" />
                        Add to dashboard
                      </button>
                    )}
                    <button
                      onClick={() => handleDownloadChart(`refine-chat-chart-${i}`, (msg.chart as ChartSpec).title)}
                      className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <Download className="w-3.5 h-3.5" />
                      Download
                    </button>
                  </div>
                </div>
              ) : (
                <div className="w-full rounded-xl px-4 py-3 bg-gray-50 border border-gray-200 text-gray-700 text-sm prose prose-sm max-w-none">
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_TABLE_COMPONENTS}>{msg.content}</ReactMarkdown>
                </div>
              )}
            </div>
          ))}
          {chatLoading && (
            <div className="flex justify-start">
              <div className="bg-gray-50 border border-gray-200 rounded-2xl px-4 py-3 flex items-center gap-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />
                <span className="text-xs text-gray-400">Thinking…</span>
              </div>
            </div>
          )}
          {pptxGenerating && (
            <div className="flex justify-start">
              <div className="bg-indigo-50 border border-indigo-200 rounded-2xl px-4 py-3 flex items-center gap-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin text-orange-500" />
                <span className="text-xs text-orange-600 font-medium">Generating presentation… this may take a few minutes</span>
              </div>
            </div>
          )}
          {excelGenerating && (
            <div className="flex justify-start">
              <div className="bg-green-50 border border-green-200 rounded-2xl px-4 py-3 flex items-center gap-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin text-green-500" />
                <span className="text-xs text-green-600 font-medium">Generating Excel file…</span>
              </div>
            </div>
          )}
          {chartGenerating && (
            <div className="flex justify-start">
              <div className="bg-indigo-50 border border-indigo-200 rounded-2xl px-4 py-3 flex items-center gap-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin text-indigo-500" />
                <span className="text-xs text-indigo-600 font-medium">Building chart…</span>
              </div>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>

        {/* Mode toggle + input */}
        <div className="border-t border-gray-100 bg-white flex-shrink-0">
          {/* Mode toggle */}
          <div className="flex items-center gap-1 px-4 pt-3 flex-wrap">
            {availableModes.map(m => {
              const metaMap: Record<ChatMode, { Icon: typeof MessageSquare; label: string; color: string }> = {
                chat:  { Icon: MessageSquare, label: 'Chat',         color: accentColor },
                chart: { Icon: BarChart2,     label: 'Chart',        color: CHART_COLOR },
                pptx:  { Icon: Presentation,  label: 'Presentation', color: PPTX_COLOR  },
                excel: { Icon: Download,      label: 'Excel',        color: EXCEL_COLOR },
                word:  { Icon: FileText,      label: 'Word',         color: WORD_COLOR  },
              };
              const meta = metaMap[m];
              const isActive = chatMode === m;
              return (
                <button
                  key={m}
                  onClick={() => setChatMode(m)}
                  className={`flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${isActive ? 'text-white shadow-sm' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}
                  style={isActive ? { backgroundColor: meta.color } : {}}
                >
                  <meta.Icon className="w-3.5 h-3.5" /> {meta.label}
                </button>
              );
            })}
          </div>

          {/* Textarea + send */}
          <div className="px-4 py-3 flex items-end gap-2">
            <textarea
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                e.target.style.height = 'auto';
                e.target.style.height = `${Math.min(e.target.scrollHeight, 200)}px`;
              }}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              placeholder={placeholderByMode[chatMode]}
              rows={1}
              disabled={chatLoading || pptxGenerating || excelGenerating || wordGenerating || chartGenerating}
              className="flex-1 px-3 py-3 text-sm border border-gray-200 rounded-xl resize-none focus:outline-none focus:border-gray-400 disabled:opacity-50 leading-relaxed"
              style={{ minHeight: '48px', maxHeight: '200px' }}
              autoFocus
            />
            <button
              onClick={() => {
                // Word mode supports "empty send" — user can just click to
                // download the full analysis as-is without typing a refinement.
                if (chatMode === 'word' && !input.trim()) {
                  handleGenerateWord('');
                  return;
                }
                handleSend();
              }}
              disabled={chatLoading || pptxGenerating || excelGenerating || wordGenerating || chartGenerating || (chatMode !== 'word' && !input.trim())}
              className="h-12 px-4 flex-shrink-0 flex items-center justify-center gap-1.5 rounded-xl text-white text-sm font-semibold disabled:opacity-40 transition-colors"
              style={{ backgroundColor: sendColor }}
            >
              {chatMode === 'pptx'
                ? <><Presentation className="w-4 h-4" /> .pptx</>
                : chatMode === 'excel'
                  ? <><Download className="w-4 h-4" /> .xlsx</>
                  : chatMode === 'word'
                    ? <><FileText className="w-4 h-4" /> .docx</>
                    : chatMode === 'chart'
                      ? <><BarChart2 className="w-4 h-4" /> Chart</>
                      : <Send className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default RefineChatPanel;
